// Before generating an AVR preview PDF, verify that the two independently
// fetched report sections refer to the same approved findings. This is a
// browser-side safety net, NOT proof that a PDF is immutable or authorised
// for issue. The server-side approval-time snapshot remains authoritative.
const reference = value => String(value || '').trim().toUpperCase();
const status = value => String(value || '').trim().toLowerCase();

const indexRows = (rows, refKey, statusKey, section, errors) => {
  const found = new Map();
  for (const row of rows || []) {
    const ref = reference(row && row[refKey]);
    const state = status(row && row[statusKey]);
    if (!ref) {
      errors.push(section + ' contains a finding without a reference.');
      continue;
    }
    if (found.has(ref)) {
      errors.push(section + ' contains duplicate reference ' + ref + '.');
      continue;
    }
    if (!['open', 'closed'].includes(state)) {
      errors.push(section + ' contains ' + ref + ' with an unrecognised status.');
    }
    found.set(ref, state);
  }
  return found;
};

export const compareAvrFindingSections = (registerRows, detailRows) => {
  const errors = [];
  const register = indexRows(registerRows, 'finding_ref', 'status',
    'Finding Register', errors);
  const details = indexRows(detailRows, 'findingRef', 'currentStatus',
    'Finding Details', errors);

  for (const [ref, state] of register) {
    if (!details.has(ref)) errors.push(ref + ' is missing from Finding Details.');
    else if (details.get(ref) !== state)
      errors.push(ref + ' has a different Open/Closed status in Finding Details.');
  }
  for (const ref of details.keys()) {
    if (!register.has(ref)) errors.push(ref + ' is missing from Finding Register.');
  }

  return {
    ready: errors.length === 0,
    count: register.size,
    open: [...register.values()].filter(x => x === 'open').length,
    closed: [...register.values()].filter(x => x === 'closed').length,
    errors
  };
};

/**
 * A PDF preview must not quietly contain charts with a different number of
 * Open findings than the approved Register. Nature and issue bars are NOT
 * additive: one finding may have several activity natures or issue flags.
 */
export const compareAvrFindingChartTotals = (registerRows, chart) => {
  const errors = [];
  const openRows = (registerRows || []).filter(row => status(row && row.status) === 'open');
  const actualTotal = openRows.length;

  for (const key of ['date', 'level']) {
    const section = chart && chart[key];
    const labels = section && section.labels;
    const data = section && section.datasets && section.datasets[0] && section.datasets[0].data;
    if (!Array.isArray(labels) || !Array.isArray(data) || labels.length !== data.length ||
        data.some(value => !Number.isInteger(value) || value < 0)) {
      errors.push('The Open Findings ' + key + ' chart has incomplete or invalid data.');
      continue;
    }
    if (data.reduce((sum, value) => sum + value, 0) !== actualTotal) {
      errors.push('The Open Findings ' + key +
        ' chart total does not match the approved Finding Register (' + actualTotal + ').');
    }
    if (key === 'level') {
      const expected = { Low: 0, Medium: 0, High: 0, Unclassified: 0 };
      for (const row of openRows) {
        const name = String((row && row.finding_level_summary) || '').trim().toLowerCase();
        const label = ({ low:'Low', medium:'Medium', high:'High' })[name] || 'Unclassified';
        expected[label]++;
      }
      const present = Object.fromEntries(labels.map((label, i) => [label, data[i]]));
      for (const [label, count] of Object.entries(expected)) {
        if ((present[label] || 0) !== count) {
          errors.push('The Open Findings priority chart does not match approved ' +
            label + ' findings (' + count + ').');
        }
      }
    }
  }
  return { ready: errors.length === 0, open: actualTotal, errors };
};
